import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { ProtectedRoute } from './protected-route';
import {
  RUTA_DEL_PRICER_NUEVA,
  rutaDeLaCotizacion,
} from '@/features/pricer-datec/lib/rutas-del-pricer';

/**
 * Las páginas del Pricer se cargan PEREZOSAS (`lazy`): este archivo NO las importa de forma estática, así sólo se
 * descarga la que de verdad se sirve (con el interruptor prendido, la pantalla de siempre ni se pide). Un test lo vigila.
 */
const PricerDatecEditorPage = lazy(() =>
  import('@/features/pricer-datec/pages/pricer-datec-editor-page').then((m) => ({
    default: m.PricerDatecEditorPage,
  }))
);
const PricerDatecPage = lazy(() =>
  import('@/features/pricer-datec/pages/pricer-datec-page').then((m) => ({
    default: m.PricerDatecPage,
  }))
);

const cargando = (
  <p role="status" className="p-6 text-sm text-muted-foreground">
    Cargando…
  </p>
);

/**
 * FP-4 (m-2): sin «Ver costos y márgenes» no se cotiza en el Pricer. Abrir `/pricer-datec/new` por la URL directa
 * llevaba a una pantalla que el servidor rechazaba recién al guardar: se redirige a la lista. Vale para las dos
 * pantallas (la nueva y la de siempre).
 */
export function NuevaCotizacionDatec({ children }: { children: ReactNode }) {
  const { hasPermission } = useAuth();
  if (!hasPermission('view_costs')) return <Navigate to="/pricer-datec/quotes" replace />;
  return <>{children}</>;
}

/** `/pricer-datec/editor/new|:id` (las del piloto) llevan a la ruta de siempre, con `?paso` e `?item` intactos. */
function RedirigirDelPiloto({ conId }: { conId: boolean }) {
  const { id } = useParams();
  const { search } = useLocation();
  const pathname = conId ? rutaDeLaCotizacion(id ?? '') : RUTA_DEL_PRICER_NUEVA;
  return <Navigate to={{ pathname, search }} replace />;
}

/**
 * Las rutas de la pantalla cotizadora del Pricer Datec (F5).
 *
 * - `editorNuevo` prendido (lo normal): `/pricer-datec/new` y `/pricer-datec/:id` sirven el editor nuevo. Ninguna
 *   ruta lleva a la pantalla de siempre.
 * - Apagado (`VITE_PRICER_EDITOR_NUEVO=false`, el FRENO DE EMERGENCIA): esas mismas dos rutas vuelven a servir la
 *   pantalla de siempre.
 * - En los dos casos `/pricer-datec/editor/*` redirige a las de arriba y `new` exige «Ver costos y márgenes».
 *
 * Quien arma el router las pega con `{rutasDelPricerDatec(EDITOR_NUEVO)}`.
 */
export function rutasDelPricerDatec(editorNuevo: boolean) {
  const Pantalla = editorNuevo ? PricerDatecEditorPage : PricerDatecPage;
  return [
    <Route
      key="pricer-new"
      path="/pricer-datec/new"
      element={
        <ProtectedRoute>
          <NuevaCotizacionDatec>
            <Suspense fallback={cargando}>
              <Pantalla />
            </Suspense>
          </NuevaCotizacionDatec>
        </ProtectedRoute>
      }
    />,
    <Route
      key="pricer-id"
      path="/pricer-datec/:id"
      element={
        <ProtectedRoute>
          <Suspense fallback={cargando}>
            <Pantalla />
          </Suspense>
        </ProtectedRoute>
      }
    />,
    <Route
      key="piloto-new"
      path="/pricer-datec/editor/new"
      element={<RedirigirDelPiloto conId={false} />}
    />,
    <Route
      key="piloto-id"
      path="/pricer-datec/editor/:id"
      element={<RedirigirDelPiloto conId />}
    />,
  ];
}
